import React, { useState, useEffect } from 'react';
import { User, Role } from '../types';
import { Users, UserPlus, Edit, Trash2, Check, X, Shield, Clock, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Partial<User>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Load users and roles
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        const { data: rolesData, error: rolesError } = await supabase
          .from('roles')
          .select('*');
        
        if (rolesError) throw rolesError;
        setRoles(rolesData);

        // Load users and their status
        const { data: usersData, error: usersError } = await supabase
          .from('user_management_view')
          .select('*');

        if (usersError) throw usersError;
        
        // Separate pending and active users
        const pending = usersData.filter(user => 
          user.approval_status === 'pending' || user.approval_status === 'rejected'
        );
        
        const active = usersData.filter(user => 
          !user.approval_status || user.approval_status === 'approved'
        ).map(user => ({
          id: user.user_id,
          email: user.email,
          role: user.role_name,
          created_at: user.created_at
        }));

        setUsers(active);
        setPendingUsers(pending);

      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Add new user
  const handleAddUser = async () => {
    try {
      if (!selectedUser.email || !selectedUser.role) {
        setError('Please fill in all required fields');
        return;
      }

      // Create user
      // Add to pending users
      const { error: pendingError } = await supabase
        .from('pending_users')
        .insert({
          email: selectedUser.email,
          requested_role: selectedUser.role
        });

      if (pendingError) throw pendingError;

      // Update local state
      setPendingUsers([...pendingUsers, {
        email: selectedUser.email,
        requested_role: selectedUser.role,
        status: 'pending',
        created_at: new Date().toISOString()
      }]);

      setIsAddingUser(false);
      setSelectedUser({});
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle approving a user
  const handleApproveUser = async (email: string, role: string) => {
    try {
      const { error } = await supabase.rpc('approve_user', {
        p_email: email,
        p_role: role
      });

      if (error) throw error;

      // Update local state
      setPendingUsers(pendingUsers.filter(user => user.email !== email));
      loadData(); // Reload all data to get updated user list
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle rejecting a user
  const handleRejectUser = async (email: string) => {
    try {
      const { error } = await supabase.rpc('reject_user', {
        p_email: email
      });

      if (error) throw error;

      // Update local state
      setPendingUsers(pendingUsers.map(user => 
        user.email === email 
          ? { ...user, status: 'rejected' }
          : user
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  // Update user role
  const handleUpdateUser = async () => {
    try {
      if (!selectedUser.id || !selectedUser.role) {
        setError('Please select a role');
        return;
      }

      // Get role ID
      const { data: roleData } = await supabase
        .from('roles')
        .select('id')
        .eq('name', selectedUser.role)
        .single();

      // Update role
      const { error: updateError } = await supabase
        .from('user_roles')
        .update({ role_id: roleData.id })
        .eq('user_id', selectedUser.id);

      if (updateError) throw updateError;

      // Update local state
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { ...user, role: selectedUser.role }
          : user
      ));

      setIsEditingUser(false);
      setSelectedUser({});
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId: string) => {
    try {
      // Delete user from auth
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      if (authError) throw authError;

      // Role assignment will be automatically deleted due to foreign key constraint

      // Update local state
      setUsers(users.filter(user => user.id !== userId));
      setShowDeleteConfirm(null);
    } catch (err) {
      setError(err.message);
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#ff8200]">User Management</h1>
          <p className="text-gray-600 mt-1">Manage users and their roles</p>
        </div>
        <button
          onClick={() => setIsAddingUser(true)}
          className="btn-primary flex items-center"
        >
          <UserPlus className="h-5 w-5 mr-1" />
          Add User
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Pending Users Section */}
      {pendingUsers.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 text-[#ff8200]">Pending Approvals</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-[#ffead5]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                    Requested Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pendingUsers.map((user) => (
                  <tr key={user.email}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-[#ff8200] mr-2" />
                        <span>{user.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.requested_role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.status === 'pending' && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleApproveUser(user.email, user.requested_role)}
                            className="text-green-600 hover:text-green-800"
                            title="Approve"
                          >
                            <Check className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleRejectUser(user.email)}
                            className="text-red-600 hover:text-red-800"
                            title="Reject"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-[#ffead5]">
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#ff8200] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-[#ff8200] mr-2" />
                      <span>{user.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'super_admin' 
                        ? 'bg-purple-100 text-purple-800'
                        : user.role === 'admin'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                      <Shield className="h-3 w-3 mr-1" />
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setIsEditingUser(true);
                        }}
                        className="text-[#ff8200] hover:text-[#e67600]"
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      
                      {showDeleteConfirm === user.id ? (
                        <>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Check className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => setShowDeleteConfirm(user.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {(isAddingUser || isEditingUser) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4 text-[#ff8200]">
              {isAddingUser ? 'Add New User' : 'Edit User Role'}
            </h2>
            
            <div className="space-y-4">
              {isAddingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email*
                  </label>
                  <input
                    type="email"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                    value={selectedUser.email || ''}
                    onChange={e => setSelectedUser({ ...selectedUser, email: e.target.value })}
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role*
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#ff8200] focus:border-[#ff8200] outline-none"
                  value={selectedUser.role || ''}
                  onChange={e => setSelectedUser({ ...selectedUser, role: e.target.value })}
                >
                  <option value="">Select a role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setIsAddingUser(false);
                  setIsEditingUser(false);
                  setSelectedUser({});
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={isAddingUser ? handleAddUser : handleUpdateUser}
                className="btn-primary"
              >
                {isAddingUser ? 'Add User' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;